import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';
import { usePreferencesStore, type Locale } from '../../stores/preferences.js';
import { i18nInstance } from '../../lib/i18n.js';
import { pushToast } from '../app/toast-store.js';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Español' },
] as const;

export function LanguageSection() {
  const { t } = useTranslation('settings');
  const locale = usePreferencesStore((s) => s.locale);
  const setLocale = usePreferencesStore((s) => s.setLocale);

  useEffect(() => {
    if (i18nInstance.language !== locale) {
      void i18nInstance.changeLanguage(locale);
    }
  }, []);

  const currentLabel =
    LANGUAGE_OPTIONS.find((o) => o.value === locale)?.label ?? LANGUAGE_OPTIONS[0].label;

  async function handleChange(newLocale: Locale) {
    try {
      await i18nInstance.changeLanguage(newLocale);
      setLocale(newLocale);
      pushToast({
        tone: 'success',
        title: i18nInstance.t('language.saved', { ns: 'settings' }),
      });
    } catch {
      pushToast({ tone: 'error', title: t('language.saved') });
    }
  }

  return (
    <section aria-labelledby="settings-language-heading" className="mb-2">
      <h2 id="settings-language-heading" className="text-h2 font-semibold text-primary mb-4">
        {t('language.heading')}
      </h2>
      <Listbox value={locale} onChange={handleChange}>
        <p className="block text-small text-secondary uppercase tracking-wider mb-1">
          {t('language.label')}
        </p>
        <ListboxButton
          data-testid="language-listbox"
          data-locale={locale}
          className="w-full bg-transparent border-0 border-b border-white/[.04] focus:border-white focus:outline-none px-0 py-2 text-primary text-left flex items-center justify-between"
        >
          <span>{currentLabel}</span>
          <ChevronDown className="h-4 w-4 text-secondary" aria-hidden="true" />
        </ListboxButton>
        <ListboxOptions className="mt-2 bg-white/[.03] backdrop-blur-sm border border-white/[.04] rounded-lg overflow-hidden focus:outline-none z-10 transition data-[closed]:opacity-0 data-[open]:opacity-100 motion-safe:duration-100">
          {LANGUAGE_OPTIONS.map((opt) => (
            <ListboxOption
              key={opt.value}
              value={opt.value}
              className="group px-4 py-2 text-body text-primary cursor-pointer data-[focus]:bg-white/[.05] data-[selected]:text-accent-lime"
            >
              {opt.label}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </section>
  );
}
